import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

/**
 * URL-synced state for dashboard filters.
 *
 * Goal: a URL is a complete, reproducible snapshot of what the PM is
 * looking at. Paste it to another PM, they see the same numbers.
 *
 * Two flavors of default:
 *   - **Non-empty default** (e.g. `from = today - 120 days`): hydrated
 *     into the URL on first render so a shared URL is reproducible
 *     regardless of when it's opened. Never automatically stripped.
 *   - **Empty-string default** (e.g. `pattern = ""`, `selectedCode = ""`):
 *     stays out of the URL until set, and is removed when cleared. Empty
 *     means "no filter applied" — no need to clutter the URL with it.
 *
 * Defaults are frozen on first render so dynamic defaults like
 * `isoDaysAgo(120)` don't drift across re-renders.
 *
 * `replace: true` — back-button doesn't pile up per-keystroke history.
 *
 * Constraint: values are strings. ISO dates work as-is. For booleans use
 * `'1'`/`''`. For numbers use the string form. One URL param per filter.
 */
export function useDashboardUrlState<T extends Record<string, string>>(
  initialDefaults: T
): readonly [T, (updates: Partial<T>) => void] {
  const [defaults] = useState(initialDefaults);
  const [params, setParams] = useSearchParams();

  // On mount, write any non-empty defaults that aren't already in the URL,
  // so the URL becomes a full snapshot of the visible view.
  useLayoutEffect(() => {
    const next = new URLSearchParams(params);
    let changed = false;
    for (const key of Object.keys(defaults) as (keyof T)[]) {
      if (defaults[key] !== "" && next.get(key as string) == null) {
        next.set(key as string, defaults[key] as string);
        changed = true;
      }
    }
    if (changed) setParams(next, { replace: true });
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const state = useMemo(() => {
    const out = { ...defaults } as T;
    for (const key of Object.keys(defaults) as (keyof T)[]) {
      const v = params.get(key as string);
      if (v != null) out[key] = v as T[typeof key];
    }
    return out;
  }, [params, defaults]);

  const update = useCallback(
    (updates: Partial<T>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of Object.keys(updates) as (keyof T)[]) {
            const v = updates[key];
            const isEmpty = v == null || v === "";
            if (isEmpty && defaults[key] === "") {
              // Empty-default key, cleared → drop from URL.
              next.delete(key as string);
            } else if (isEmpty) {
              // Non-empty-default key, cleared → reset to the default,
              // keep it in the URL so the snapshot stays complete.
              next.set(key as string, defaults[key] as string);
            } else {
              next.set(key as string, String(v));
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setParams, defaults]
  );

  return [state, update] as const;
}
